import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export type BookIdea = {
  id: string;
  title: string;
  description: string;
  genre: string[];
  custom_tags: string[];
  cover_image: string | null;
  created_at: string;
  author_id: string;
  identity_id: string;
  identity_username: string;
  identity_avatar_url: string | null;
  votes_count: number;
  comments_count: number;
  followers_count: number;
  has_published_milestone: boolean;
};

export function useBookIdeas() {
  const [ideas, setIdeas] = useState<BookIdea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchIdeas();
  }, []);

  async function fetchIdeas() {
    try {
      const { data, error } = await supabase
        .from('book_ideas')
        .select(`
          *,
          votes:votes(count),
          comments:comments(count),
          followers:idea_follows(count),
          has_published_milestone:milestones(status)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const processedIdeas = (data || []).map(idea => ({
        ...idea,
        votes_count: idea.votes[0]?.count ?? 0,
        comments_count: idea.comments[0]?.count ?? 0,
        followers_count: idea.followers[0]?.count ?? 0,
        has_published_milestone: idea.has_published_milestone?.some(m => m.status === 'completed') ?? false
      }));

      // Sort ideas: unpublished first, then published
      const sortedIdeas = processedIdeas.sort((a, b) => {
        if (a.has_published_milestone === b.has_published_milestone) {
          // Within each group, sort by creation date (newest first)
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }
        // Put unpublished ideas first
        return a.has_published_milestone ? 1 : -1;
      });

      setIdeas(sortedIdeas);
      setError(null);
    } catch (err) {
      console.error('Error fetching book ideas:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch book ideas');
      setIdeas([]);
    } finally {
      setLoading(false);
    }
  }

  return { ideas, loading, error };
}